/**
 * The right-hand card: the form the trainer has pointed at, its questions, and the one way to change
 * it.
 *
 * ── It DESCRIBES, it does not edit ───────────────────────────────────────────────────────────
 * The pencil opens `/raporty/$id` and that is the ONE control on this card. `model/surveyDetail`'s
 * header carries the argument: two controls that both change a form, a centimetre apart, is the
 * pair a trainer presses the wrong half of — and a toggle that wrote on press would contradict the
 * editor's own rule, where including a question is a checkbox confirmed by „Zapisz zmiany" because
 * the PATCH replaces the whole item list at once.
 *
 * „Podgląd" stood beside the pencil until 17 September 2026 and opened the last filled-in copy of
 * this form. The owner removed it — „nie ma on sensu tutaj": this card describes a FORM, and the
 * one thing it could say about a submission was whichever client happened to answer most recently,
 * which is a question „Do przejrzenia" and the client's own Ankiety tab both answer properly. The
 * dialog itself stays in `features/survey-report`, where the queue opens it.
 *
 * ── `pending`, `error` and `none` are three states, and all three stand INSIDE the card ──────
 * The card's box is the stylesheet's, so it never changes with its state — and its head holds a
 * reserve, so a headline landing does not push the sections down.
 *
 * ── The card hands over as ONE box ───────────────────────────────────────────────────────────
 * The head and the sections both fork on `shown`, which is the row ONLY once the card is out of its
 * gated wait. The row alone comes from the SELECTION, which is resolved the moment the list's read
 * answers — up to 300ms before the gate retires the bars beneath it — so a headline would stand
 * over a skeleton body, and over a column of skeletons beside it, for the length of the hand-over.
 */

import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';

import { EmptyState } from '~/shared/ui/EmptyState';
import { NoteMarkIcon } from '~/shared/ui/EmptyStateIcons';
import ember from '~/shared/ui/emberButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import {
  QuestionGlyph,
  sectionSubtitle,
  sectionTitle,
  type DetailSection,
  type SurveySection,
} from '~/features/survey-question-editor';
import type { SurveyRow } from '../model/surveyList';
import type { SurveyDetailState } from '../lib/useSurveysTab';
import {
  DumbbellIcon,
  HeartIcon,
  PencilIcon,
  PersonIcon,
  SparkIcon,
} from '~/shared/ui/ReportIcons';
import { RequiredMark } from '~/shared/ui/RequiredMark';
import canon from '~/shared/ui/surveyCanon.module.css';
import styles from './SurveyDetailCard.module.css';

/** One tile per section, drawn from the section's own name. A table, so a new section is a compile error. */
const SECTION_TILE: Record<SurveySection, (props: { className?: string }) => React.JSX.Element> = {
  personal: PersonIcon,
  health: HeartIcon,
  training: DumbbellIcon,
  wellbeing: SparkIcon,
  additional: SparkIcon,
};

/** Two, the canon's own column count — so the wait fills the card the way the sections will. */
const SKELETON_SECTIONS = 2;
const SKELETON_ROWS = 3;

function DetailSkeleton({ skeleton }: { readonly skeleton: boolean }) {
  const { t } = useTranslation();

  return (
    <div className={styles.body} aria-busy="true">
      <p className={hidden.srOnly}>{t('web:reports.surveys.detail.loading')}</p>
      {Array.from({ length: SKELETON_SECTIONS }, (_, section) => (
        <div key={section} className={`${String(styles.section)} ${String(styles.skelSection)}`}>
          <div className={styles.sectionHead}>
            <i className={canon.tile} />
            <span className={styles.sectionText}>
              {skeleton && (
                <>
                  <i className={`${String(skel.bar)} ${String(styles.skelTitle)}`} />
                  <i className={`${String(skel.bar)} ${String(styles.skelSub)}`} />
                </>
              )}
            </span>
          </div>
          <ul className={styles.questions}>
            {Array.from({ length: SKELETON_ROWS }, (_, row) => (
              <li key={row} className={styles.question}>
                {skeleton && (
                  <span className={styles.questionText}>
                    <i className={`${String(skel.bar)} ${String(styles.skelLabel)}`} />
                    <i className={`${String(skel.bar)} ${String(styles.skelType)}`} />
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function Sections({ sections, reveal }: { readonly sections: readonly DetailSection[]; readonly reveal: string }) {
  const { t } = useTranslation();

  return (
    <div className={`${String(styles.body)} ${reveal}`}>
      {sections.map((entry) => {
        const Tile = SECTION_TILE[entry.section];
        return (
          <section className={styles.section} key={entry.section}>
            <div className={styles.sectionHead}>
              <span className={canon.tile}>
                <Tile className={canon.tileIcon} />
              </span>
              <span className={styles.sectionText}>
                <h3 className={styles.sectionTitle}>{sectionTitle(entry.section, t)}</h3>
                <span className={styles.sectionSubtitle}>
                  {sectionSubtitle(entry.section, t)}
                </span>
              </span>
              <span className={styles.sectionCount}>{entry.questions.length}</span>
            </div>
            <ul className={styles.questions}>
              {entry.questions.map((question) => (
                <li className={styles.question} key={question.key}>
                  <span className={styles.questionText}>
                    <span className={styles.questionLabel}>
                      {/* The same mark the editor's row draws, from the same mapping — the two
                        * surfaces list the same rows and one of them marking them would be the pair
                        * disagreeing about one form. */}
                      <QuestionGlyph glyph={question.glyph} className={styles.questionGlyph} />
                      {question.label}
                      {question.required ? (
                        <RequiredMark label={t('web:reports.surveys.detail.required')} />
                      ) : null}
                    </span>
                    <span className={styles.questionMeta}>
                      {question.typeLabelKey === null ? null : (
                        <span className={styles.type}>{t(question.typeLabelKey)}</span>
                      )}
                      {question.mapNoteKey === null ? null : (
                        <span className={styles.mapNote}>{t(question.mapNoteKey)}</span>
                      )}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

interface SurveyDetailCardProps {
  readonly state: SurveyDetailState;
  readonly skeleton: boolean;
  readonly reveal: string;
  readonly row: SurveyRow | null;
  readonly sections: readonly DetailSection[];
}

export function SurveyDetailCard({
  state,
  skeleton,
  reveal,
  row,
  sections,
}: SurveyDetailCardProps) {
  const { t } = useTranslation();

  // The row once the wait is over, and `null` for the whole of it. See the header.
  const shown = state === 'pending' ? null : row;

  if (shown === null) {
    // No form to describe yet. The head is DRAWN EMPTY rather than left out, so the card holds the
    // same shape through the wait and the sections do not move up and back down when one arrives.
    return (
      <section className={styles.card} aria-label={t('web:reports.surveys.detail.noneTitle')}>
        <div className={styles.head} />
        {state === 'pending' ? (
          <DetailSkeleton skeleton={skeleton} />
        ) : (
          <div className={styles.state}>
            <EmptyState
              mark={<NoteMarkIcon />}
              tone="neutral"
              title={t(
                state === 'error'
                  ? 'web:reports.surveys.detail.errorTitle'
                  : 'web:reports.surveys.detail.noneTitle',
              )}
              hint={t(
                state === 'error'
                  ? 'web:reports.surveys.detail.errorMessage'
                  : 'web:reports.surveys.detail.noneMessage',
              )}
            />
          </div>
        )}
      </section>
    );
  }

  // The intake wears the canon's own eyebrow, headline and lede; a check-in wears its own name,
  // because nothing in the canon writes a sentence for one and this screen does not invent copy.
  const eyebrow = shown.mandatory
    ? t('web:reports.surveys.detail.intakeEyebrow')
    : shown.leadForm === true
      ? t('web:publicForm.builder.reports.eyebrow')
      : t('web:reports.surveys.detail.checkInEyebrow');
  const headline = shown.mandatory ? t('web:reports.surveys.detail.intakeHeadline') : shown.title;

  return (
    <section className={styles.card} aria-label={shown.title}>
      <div className={`${String(styles.head)} ${reveal}`}>
        <div className={styles.headText}>
          <span className={canon.eyebrow}>{eyebrow}</span>
          <h2 className={styles.headline}>{headline}</h2>
          {shown.mandatory ? (
            <p className={styles.lede}>{t('web:reports.surveys.detail.intakeLede')}</p>
          ) : null}
        </div>

        <Link
          to="/raporty/$templateId"
          params={{ templateId: String(shown.id) }}
          className={`${String(ember.ember)} ${String(styles.edit)}`}
          aria-label={t('web:reports.surveys.detail.edit')}
          title={t('web:reports.surveys.detail.edit')}
        >
          <PencilIcon className={styles.editIcon} />
        </Link>
      </div>

      {state === 'error' ? (
        <div className={styles.state}>
          <EmptyState
            mark={<NoteMarkIcon />}
            tone="neutral"
            title={t('web:reports.surveys.detail.errorTitle')}
            hint={t('web:reports.surveys.detail.errorMessage')}
          />
        </div>
      ) : (
        <Sections sections={sections} reveal={reveal} />
      )}
    </section>
  );
}
